/**
 * src/shell/agents/services/HtmlSanitizer.ts
 *
 * Strict AST-based HTML & CSS Sanitizer for LLM-Generated Web Content.
 * Prevents Remote Code Execution and XSS in Desktop/Electron/Web environments.
 */

export const ALLOWED_HTML_TAGS = new Set([
  'html', 'head', 'body', 'meta', 'title', 'style',
  'div', 'section', 'header', 'footer', 'nav', 'main', 'article', 'aside',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'a', 'img', 'svg', 'path',
  'ul', 'ol', 'li', 'button', 'form', 'input', 'label', 'textarea', 'select', 'option',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'strong', 'em', 'b', 'i', 'u', 's', 'code', 'pre', 'blockquote', 'hr', 'br'
]);

export const FORBIDDEN_ATTR_PATTERNS = [
  /^on[a-z]+/i, // onclick, onerror, onload, onmouseover, onfocus, etc.
  /^srcdoc$/i,
  /^formaction$/i,
  /^background$/i
];

export const FORBIDDEN_URL_PROTOCOLS = [
  'javascript:',
  'vbscript:',
  'data:text/html',
  'data:application/javascript',
  'data:text/javascript'
];

/**
 * Sanitizes LLM-generated HTML by removing:
 * 1. Malicious executable script/iframe/embed/object tags
 * 2. Event handler attributes (onerror, onload, onclick, onmouseover...)
 * 3. Dangerous URI schemes (javascript:, vbscript:, data:text/html)
 * 4. Node.js/Electron specific code injections (require, process, child_process)
 */
export function sanitizeGeneratedHTML(rawHtml: string): string {
  if (!rawHtml || typeof rawHtml !== 'string') return '';

  let sanitized = rawHtml;

  // 1. Remove dangerous executable tags
  sanitized = sanitized.replace(/<\s*(script|iframe|object|embed|applet|meta\s+http-equiv)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '');
  sanitized = sanitized.replace(/<\s*(script|iframe|object|embed|applet|meta\s+http-equiv)[^>]*\/?\s*>/gi, '');

  // 2. Remove all on* event handler attributes
  sanitized = sanitized.replace(/\s+on[a-zA-Z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');

  // 3. Neutralize dangerous URL schemes in href and src
  for (const protocol of FORBIDDEN_URL_PROTOCOLS) {
    const regex = new RegExp(`(href|src)\\s*=\\s*(['"])\\s*${protocol}`, 'gi');
    sanitized = sanitized.replace(regex, '$1=$2#blocked-protocol');
  }

  // 4. Scrub any direct Electron/Node code leakage
  sanitized = sanitized.replace(/require\s*\([^)]*\)/gi, '[BLOCKED_EXECUTION]');
  sanitized = sanitized.replace(/\b(process\.env|child_process|fs\.|path\.|electron\.)\b/gi, '[BLOCKED_EXECUTION]');

  return sanitized;
}

/**
 * Wraps HTML in a sandboxed HTML5 boilerplate ready for iframe srcDoc preview.
 */
export function wrapInSandboxDoc(contentHtml: string, title = 'Landing Page Preview'): string {
  const safeContent = sanitizeGeneratedHTML(contentHtml);
  if (safeContent.includes('<!DOCTYPE html>') || safeContent.includes('<html')) {
    return safeContent;
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; line-height: 1.5; color: #1e293b; background: #0f172a; }
  </style>
</head>
<body>
  ${safeContent}
</body>
</html>`;
}
